import { FC, ReactNode, useMemo } from 'react';
import { maxBy } from 'lodash';

import { FileResponse, FileRevision } from '@playq/octopus2-files';

import { SelectSearchList } from '/shared/SelectSearchList';
import { ISelectedFile } from '/component/Files/FilePicker';

export interface IVersionPickerProps {
  file: FileResponse;
  selectedRevision?: FileRevision;
  isLink?: boolean;
  emptyPlaceholder?: string;
  renderLabel?: (revision: FileRevision) => ReactNode;
  onSelect?: (selectedFile: ISelectedFile) => void;
}

const renderLabelComment = (rev: FileRevision) => rev.comment;
const getVersionItem = (rev: FileRevision) => ({
  label: rev.comment ? `${rev.version} - ${rev.comment}` : `${rev.version}`,
  value: rev.version,
});

export const VersionsPicker: FC<IVersionPickerProps> = (props) => {
  const { file, selectedRevision, isLink = true, emptyPlaceholder, renderLabel = renderLabelComment, onSelect } = props;

  const lastRevision = useMemo(() => {
    return maxBy(file.revisions, (r: FileRevision) => r.version) as FileRevision;
  }, [file]);

  const versionList = useMemo(() => {
    return file.revisions
      .sort((a: FileRevision, b: FileRevision) => b.version - a.version)
      .map((rev: FileRevision) => getVersionItem(rev));
  }, [file.revisions]);

  const handleItemSelect = (_: unknown, version: number) => {
    if (onSelect) {
      const revision = file.revisions[version];
      onSelect({ fileRevision: revision, fileResponse: file });
    }
  };

  const selected = selectedRevision || lastRevision;
  const label = selectedRevision ? renderLabel(selectedRevision) : (emptyPlaceholder ?? renderLabel(selected));

  return (
    <SelectSearchList
      view={isLink ? 'link' : 'default'}
      label={label}
      itemList={versionList}
      placeholder='Find version'
      onItemSelect={handleItemSelect}
    />
  );
};
